import flatten from 'lodash/flatten';

export const deepCopy = (obj) => {
    if (obj === null || typeof obj !== 'object') {
        return obj;
    }

    const copy = Array.isArray(obj) ? [] : {};

    Object.keys(obj).forEach((key) => {
        copy[key] = deepCopy(obj[key]);
    });

    return copy;
};

export const arrayWrap = (value) => {
    return Array.isArray(value) ? value : [value];
};

export const find = (selector, context = null) => {
    if (typeof selector !== 'string') {
        return selector;
    }

    context = context || document;

    return context.querySelector(selector);
};

export const kebabCase = (string) =>
    string
        .replace(/([a-z])([A-Z])/g, '$1-$2')
        .replace(/\s+/g, '-')
        .toLowerCase();

export const findMany = (selector, context = null) => {
    context = context || document;

    const results = [];

    arrayWrap(selector).forEach((selector) => {
        Array.from(context.querySelectorAll(selector)).forEach((element) => {
            results.push(element);
        });
    });

    return results;
};

// eslint-disable-next-line max-params
export const on = (event, callback, context = null, passive = true, once = false) => {
    context = context || document;
    context.addEventListener(event, callback, {
        passive,
        once,
    });
};

export const off = (event, callback, context = null) => {
    context = context || document;
    context.removeEventListener(event, callback);
};

export const fire = (event, context, detail = null) => {
    context = context || document;
    try {
        const eventObject = detail ? new CustomEvent(event, {detail}) : new Event(event);

        context.dispatchEvent(eventObject);
    } catch (e) {
        console.log(e);
    }
};

export const addClass = (element, className) => {
    const classes = arrayWrap(className),
        elements = arrayWrap(element);

    elements.forEach((node) => {
        const n = typeof node === 'string' ? find(node) : node;

        classes.forEach((classToAdd) => {
            if (n && n.classList) {
                n.classList.add(classToAdd);
            }
        });
    });
};

export const removeClass = (element, className) => {
    const classes = arrayWrap(className),
        elements = arrayWrap(element);

    elements.forEach((node) => {
        const n = typeof node === 'string' ? find(node) : node;

        classes.forEach((classToAdd) => {
            if (n && n.classList) {
                n.classList.remove(classToAdd);
            }
        });
    });
};

export const hasClass = (element, className) => {
    if (!element.classList) {
        return false;
    }

    return element.classList.contains(className.replace('.', ''));
};

export const is = (element, selector) => {
    if (selector.startsWith('.') && hasClass(element, selector)) {
        return true;
    }

    if (selector.startsWith('#') && element.id === selector.replace('#', '')) {
        return true;
    }

    if (selector.startsWith('type:')) {
        if (selector.replace('type:', '').toUpperCase() === element.nodeName) {
            return true;
        }
    }
};

export const hasParent = (element, selector) => {
    if (!element.parentNode) {
        return false;
    }

    if (is(element.parentNode, selector)) {
        return true;
    }

    return hasParent(element.parentNode, selector);
};

export const isOrHasParent = (element, selector) => {
    if (is(element, selector)) {
        return true;
    }

    return hasParent(element, selector);
};

export const dataset = (element, name, fallback = null) => {
    let content = '';

    if (element.dataset) {
        content = element.dataset[name] || fallback;
    } else {
        content = element.getAttribute(`data-${kebabCase(name)}`) || fallback;
    }

    if ((content || '').indexOf('[') === 0 || (content || '').indexOf('{') === 0) {
        content = jsonParse(content);
    }

    return content || fallback;
};

export const jsonParse = (string) => {
    let potentialJson = string || '';

    if (typeof potentialJson === 'object') {
        return potentialJson;
    }

    if (typeof potentialJson !== 'string' || (potentialJson.indexOf('{') === -1 && potentialJson.indexOf('[') === -1)) {
        return string;
    }

    try {
        return JSON.parse(potentialJson);
    } catch (e) {
        console.log(e);

        return string;
    }
};

const injectedScripts = [];

export const loadScript = (src) => {
    if (injectedScripts.indexOf(src) !== -1) {
        return Promise.resolve();
    }

    injectedScripts.push(src);

    return new Promise((success, fail) => {
        const position = find('#mainScript'),
            script = document.createElement('script');

        script.onload = function () {
            success();
        };

        script.onerror = function () {
            fail();
        };

        script.src = src;
        script.async = true;
        script.defer = true;
        position.parentNode.appendChild(script);
    });
};

// eslint-disable-next-line max-params
export const whenElementIsInView = (selectors, callback, once = false, options = {}) => {
    let elements = arrayWrap(selectors).map((selector) => {
        if (typeof selector === 'string') {
            return findMany(selector);
        }

        return arrayWrap(selector);
    });

    elements = flatten(elements);

    if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const inView = entry.isIntersecting;

                    if (inView && once) {
                        observer.unobserve(entry.target);
                    }

                    return callback(inView);
                });
            },
            {
                rootMargin: options['margin'] || '0px',
                threshold: options['threshold'] || [0, 0.25, 0.5, 0.75, 1],
            },
        );

        return elements.forEach((element) => {
            observer.observe(element);
        });
    }

    // Browser doesn't support IO so we just execute the callback.
    return callback(true);
};

export const offset = (elem) => {
    if (!elem.getClientRects().length) {
        return {top: 0, left: 0};
    }

    // Get document-relative position by adding viewport scroll to viewport-relative gBCR
    const rect = elem.getBoundingClientRect();
    const win = elem.ownerDocument.defaultView;

    return {
        top: rect.top + win.pageYOffset,
        left: rect.left + win.pageXOffset,
        width: rect.width,
        height: rect.height,
    };
};

export const ucwords = (words) => {
    return (words + '').replace(/^(.)|\s+(.)/g, function ($1) {
        return $1.toUpperCase();
    });
};

export const isTouchDevice = () => 'ontouchstart' in window || navigator.maxTouchPoints > 0 || navigator.msMaxTouchPoints > 0;

export const language = window && window.i18n && window.i18n.language ? window.i18n.language : 'en-gb';
